import { usePodLabel } from "@app/components/assistant/conversation/tool_validation/usePodLabel";
import { inferProjectTaskSourceFromUrl } from "@app/lib/api/actions/servers/pod_tasks/source_utils";
import type { PodTasksCreateTasksInput } from "@app/lib/api/actions/servers/pod_tasks/types";
import { useMemberDetails } from "@app/lib/swr/assistants";
import type { PodTaskSourceType } from "@app/types/project_task";
import type { LightWorkspaceType, UserType } from "@app/types/user";
import {
  BookOpen01,
  Checkbox,
  Chip,
  ConfluenceLogo,
  DriveLogo,
  GithubLogo,
  Icon,
  LinkExternal01,
  MessageChatSquare,
  MicrosoftLogo,
  NotionLogo,
  SlackLogo,
} from "@dust-tt/sparkle";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Plural, Trans, useLingui } from "@lingui/react/macro";
import type { ComponentType } from "react";
import { useMemo } from "react";

const SOURCE_ICON_BY_TYPE: Record<
  PodTaskSourceType,
  ComponentType<{ className?: string }>
> = {
  project_conversation: MessageChatSquare,
  project_knowledge: BookOpen01,
  slack: SlackLogo,
  notion: NotionLogo,
  gdrive: DriveLogo,
  confluence: ConfluenceLogo,
  github: GithubLogo,
  microsoft: MicrosoftLogo,
};

interface PodTasksCreateValidationDetailsProps {
  input: PodTasksCreateTasksInput;
  owner: LightWorkspaceType;
  user: UserType;
  conversationId?: string | null;
}

function formatAssigneeLabel({
  userId,
  currentUserId,
  memberDisplayById,
  isMembersLoading,
  t,
}: {
  userId: string | null | undefined;
  currentUserId: string;
  memberDisplayById: Record<string, { fullName: string }>;
  isMembersLoading: boolean;
  t: (descriptor: MessageDescriptor) => string;
}): string {
  if (userId === null || userId === undefined) {
    return t(msg`No assignee`);
  }
  if (userId === currentUserId) {
    return t(msg`You`);
  }
  const member = memberDisplayById[userId];
  if (member) {
    return member.fullName;
  }
  if (isMembersLoading) {
    return t(msg`Loading…`);
  }
  return userId;
}

export function PodTasksCreateValidationDetails({
  input,
  owner,
  user,
  conversationId,
}: PodTasksCreateValidationDetailsProps) {
  const { t } = useLingui();
  const { podLabel, isPodLabelLoading } = usePodLabel({
    owner,
    dustPodUri: input.dustPod?.uri,
    conversationId,
  });

  const assigneeIds = useMemo(
    () =>
      input.tasks
        .map((task) => task.userId)
        .filter((userId): userId is string => typeof userId === "string"),
    [input.tasks]
  );
  const { membersById, isMembersLoading } = useMemberDetails({
    workspaceId: owner.sId,
    userIds: assigneeIds,
  });

  const taskCount = input.tasks.length;
  const doneCount = input.tasks.filter((task) => task.doneRationale).length;
  const podName = isPodLabelLoading ? t`Loading…` : podLabel;

  return (
    <div className="flex flex-col gap-3 pt-2">
      <p className="text-sm text-muted-foreground">
        {input.creatorType === "user" ? (
          <Plural
            value={taskCount}
            one={
              <Trans>
                Review the{" "}
                <span className="font-medium text-foreground">#</span> task
                below before adding it to{" "}
                <span className="font-medium text-foreground">{podName}</span>.
              </Trans>
            }
            other={
              <Trans>
                Review the{" "}
                <span className="font-medium text-foreground">#</span> tasks
                below before adding them to{" "}
                <span className="font-medium text-foreground">{podName}</span>.
              </Trans>
            }
          />
        ) : (
          <Plural
            value={taskCount}
            one={
              <Trans>
                The agent wants to create{" "}
                <span className="font-medium text-foreground">#</span> task in{" "}
                <span className="font-medium text-foreground">{podName}</span>.
              </Trans>
            }
            other={
              <Trans>
                The agent wants to create{" "}
                <span className="font-medium text-foreground">#</span> tasks in{" "}
                <span className="font-medium text-foreground">{podName}</span>.
              </Trans>
            }
          />
        )}
        {doneCount > 0 && (
          <>
            {" "}
            <Plural
              value={doneCount}
              one="# will be marked as done immediately."
              other="# will be marked as done immediately."
            />
          </>
        )}
      </p>

      <div className="divide-y divide-separator overflow-hidden rounded-xl border border-separator bg-background">
        {input.tasks.map((task, index) => {
          const assigneeLabel = formatAssigneeLabel({
            userId: task.userId,
            currentUserId: user.sId,
            memberDisplayById: membersById,
            isMembersLoading,
            t,
          });
          const isDone = Boolean(task.doneRationale);

          return (
            <div
              key={`${index}-${task.text}`}
              className="flex items-start gap-3 px-3 py-3"
            >
              <div className="mt-0.5 shrink-0">
                <Checkbox checked={isDone} disabled />
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <div className="flex flex-wrap items-start gap-2">
                  <p className="min-w-0 flex-1 break-words text-sm leading-5 text-foreground">
                    {task.text}
                  </p>
                  {isDone && (
                    <Chip
                      size="xs"
                      color="success"
                      label={t({ message: "Done", context: "task status" })}
                    />
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  <Trans>Assignee: {assigneeLabel}</Trans>
                </p>
                {task.doneRationale && (
                  <p className="text-xs italic text-muted-foreground">
                    {task.doneRationale}
                  </p>
                )}
                {task.sources && task.sources.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    {task.sources.map((source) => {
                      const inferred = inferProjectTaskSourceFromUrl({
                        url: source.url,
                        title: source.title,
                      });
                      const SourceIcon =
                        SOURCE_ICON_BY_TYPE[inferred.sourceType];

                      return (
                        <a
                          key={source.url}
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex max-w-full items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs text-foreground hover:bg-muted/80"
                        >
                          <Icon
                            size="xs"
                            visual={SourceIcon}
                            className="shrink-0"
                          />
                          <span className="truncate">{source.title}</span>
                          <LinkExternal01 className="h-3 w-3 shrink-0 opacity-60" />
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
